/**
 * FolderQuotaWidget · 文件夹配额常驻入口（操作栏右簇）
 *
 * 连接型、零 props：档位用规范 vip_kind（useVipKind），used/cap 取 folderQuotaSlice。
 * 非会员/加载中/出错 → 不渲染（fail-closed）。点「升级扩容」拉开通弹窗。
 * mount + refreshTick（建/删/移）+ 升级成功后 vip_kind 变 → 自动重拉接口4。
 */
import React, { useEffect } from 'react'
import classNames from 'classnames'
import { FolderOutlined } from '@ant-design/icons'
import {
  useAppDispatch,
  useAppSelector,
  useVipKind,
  usePayment
} from '../../../../app/hooks'
import { selectFolderRefreshTick } from '../../../../slices/aiClassify'
import {
  fetchFolderQuota,
  selectFolderQuota
} from '../../../../slices/folderQuotaSlice'
import { deriveQuotaView } from '../../utils/folderQuota'
import { residentModalData } from '../../../components/FolderPaywall/paywallModalData'
import { folderPaywallCustomContent } from '../../../components/FolderPaywall/paywallModalContent'
import styles from './FolderQuotaWidget.module.scss'

const FolderQuotaWidget: React.FC = () => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s: any) => s.group.groupId)
  const vipKind = useVipKind()
  const quota = useAppSelector(selectFolderQuota)
  const refreshTick = useAppSelector(selectFolderRefreshTick)
  const payment = usePayment()

  useEffect(() => {
    // 仅会员请求；非会员不请求（结果也会被隐藏）
    if (vipKind !== 0 && groupID) {
      dispatch(fetchFolderQuota({ groupID: String(groupID) }))
    }
  }, [dispatch, groupID, vipKind, refreshTick])

  if (vipKind === 0) return null
  if (!quota.loaded || quota.loading || quota.error) return null

  const view = deriveQuotaView({
    vipKind,
    used: quota.used,
    cap: quota.cap,
    tiers: quota.tiers
  })
  if (!view.visible) return null

  return (
    <div className={classNames(styles.widget, styles[view.state])}>
      <FolderOutlined className={styles.folderIcon} />
      <span className={styles.countText}>
        文件夹个数 <strong className={styles.used}>{quota.used}</strong>
        <span className={styles.sep}>/</span>
        <span className={styles.cap}>{view.capDisplay}</span>
      </span>
      {view.showUpgrade && (
        <>
          <span className={styles.divider} />
          <button
            type="button"
            className={styles.upgrade}
            onClick={() => {
              // TODO 埋点 trackClassifyClick('upgradeQuota')（需在 ClassifyActionType 加 key）
              // source_detail 按当前会员档位（PRD §3.2.1），由 deriveQuotaView 算出
              // 开通弹窗动态文案：常驻入口（resident）—— PRD §3.4
              payment.openVipByKind(
                view.upgradeTargetVipKind,
                view.sourceDetail,
                false,
                folderPaywallCustomContent(
                  residentModalData({
                    vipKind,
                    used: quota.used,
                    cap: quota.cap,
                    tiers: quota.tiers
                  })
                )
              )
            }}
          >
            升级扩容
          </button>
        </>
      )}
    </div>
  )
}

export default FolderQuotaWidget
